// no dangerouslySetInnerHTML — titles are user-controlled text (SEC-REQ-4)
//
// V2 source path: customer/midas/components/v2/history/MidasHistoryPageV2.tsx
// V1 source: none (greenfield — Phase 4)
// Strategy: scratch
// Verified no-MUI: yes
// Phase: 4
//
// Full-page archive of all the user's MIDAS chat threads. Mounted at
// /midas-v2/history under the same MidasRouteGuard chain as /midas-v2.
//
// Layout (per spec §1):
//   • Sticky top bar: "Back to chat" link + "+ New chat" CTA
//   • Title block: "All your chat history" + mono `· N` total + filters
//   • Search input (client-side filter over loaded threads)
//   • Machine chip row (server-side filter)
//   • Date-grouped row list (TODAY / YESTERDAY / THIS WEEK / LAST WEEK / EARLIER — 4.5-fix)
//   • "Load more" button — guarded by SEC-REQ-2 offset ceiling
//
// Locked product decisions reflected here:
//   • No `?q=` server search — text filter is client-side over loaded page
//   • No `last_message_preview` field — rows show machine + title + time only
//   • Page size 50 — driven by HISTORY_PAGE_SIZE in the slice
//   • Sort updated_at DESC — server side via existing endpoint
//
// Security:
//   • Inherits MidasRouteGuard from /midas-v2 (no new auth logic)
//   • All thread filtering (userId+customerId scoping) happens server side
//     via JWT — the client never sends customer_id/user_id (SEC-REQ-1)
//   • Offset is capped at HISTORY_OFFSET_CEILING (SEC-REQ-2)
//   • Titles render through React text nodes — no HTML escapes (SEC-REQ-4)
import React, { useCallback, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";

import {
  useAppDispatch,
  useAppSelector,
} from "../../../../../shared/hooks/sharedHooks";
import useTranslation from "../../../../../shared/hooks/useTranslation";
import { useRedesignSetup } from "../../../../../shared/hooks/useRedesignSetup";
import {
  setMachineContext,
  startNewChat,
} from "../../../../../shared/chatBot/chatBotSlice";
import { getMachines } from "../../../../machinery/customerMachinerySlice";
import { Machine } from "../../../../machinery/models/models";
import { setActiveThreadId, loadThread } from "../../../midasPageSlice";
import {
  fetchHistoryThreads,
  HistoryMachineFilter,
  HISTORY_OFFSET_CEILING,
  HISTORY_PAGE_SIZE,
  loadMoreHistoryThreads,
  resetHistoryPage,
  setMachineFilter,
  setSearchQuery,
} from "../../../midasHistoryPageSlice";
import { Thread } from "../../../midasPageModels";
import { useMidasBreakpoint } from "../hooks/useMidasBreakpoint";
import MidasHistoryDateGroupV2, {
  DateBucket,
} from "./MidasHistoryDateGroupV2";
import MidasHistoryEmptyV2 from "./MidasHistoryEmptyV2";
import MidasHistoryMachineFiltersV2, {
  MachineChip,
} from "./MidasHistoryMachineFiltersV2";
import MidasHistorySearchV2 from "./MidasHistorySearchV2";
import MidasHistorySkeletonV2 from "./MidasHistorySkeletonV2";

// ════════════════════════════════════════════════════════════════
// Helpers
// ════════════════════════════════════════════════════════════════

// Sub-phase 4.5-fix (2026-05-04, D7 RE-REVISED): 5-bucket scheme.
// User testing on `.cc` with a 99-thread account showed the original
// 4.5 D7 (drop EARLIER, render TODAY/YESTERDAY/THIS WEEK only) silently
// hid ~77% of content — every thread older than start-of-this-week
// returned `null` from `bucketOf` and was dropped. The 5-bucket scheme
// (TODAY / YESTERDAY / THIS WEEK / LAST WEEK / EARLIER) mirrors the
// pattern Slack/Discord use for deep history and keeps everything
// reachable.
//
// Buckets:
//   today      — d >= startOfToday
//   yesterday  — d >= startOfYesterday    && d < startOfToday
//   thisWeek   — d >= startOfWeek         && d < startOfYesterday
//                (startOfWeek = startOfToday − 7 days)
//   lastWeek   — d >= startOfLastWeek     && d < startOfWeek
//                (startOfLastWeek = startOfWeek − 7 days)
//   earlier    — d < startOfLastWeek (open-ended tail)
//
// `bucketOf` no longer returns `null` for any input — every thread
// lands in exactly one bucket.
const bucketOf = (iso: string): DateBucket => {
  const d = new Date(iso);
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfYesterday = new Date(startOfToday);
  startOfYesterday.setDate(startOfYesterday.getDate() - 1);
  const startOfWeek = new Date(startOfToday);
  startOfWeek.setDate(startOfWeek.getDate() - 7);
  const startOfLastWeek = new Date(startOfWeek);
  startOfLastWeek.setDate(startOfLastWeek.getDate() - 7);

  if (d.getTime() >= startOfToday.getTime()) return "today";
  if (d.getTime() >= startOfYesterday.getTime()) return "yesterday";
  if (d.getTime() >= startOfWeek.getTime()) return "thisWeek";
  if (d.getTime() >= startOfLastWeek.getTime()) return "lastWeek";
  return "earlier";
};

const flattenMachines = (
  machinesMap: Record<string, Machine[] | undefined> | null | undefined
): Machine[] => {
  if (!machinesMap || typeof machinesMap !== "object") return [];
  const flat: Machine[] = [];
  Object.values(machinesMap).forEach((g) => {
    if (Array.isArray(g)) flat.push(...(g as Machine[]));
  });
  return flat;
};

// ════════════════════════════════════════════════════════════════
// MidasHistoryPageV2
// ════════════════════════════════════════════════════════════════

const MidasHistoryPageV2: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { translate } = useTranslation("chatBot");
  const { isPhone } = useMidasBreakpoint();
  // Sub-phase 4.6 §3H: inject shared `cardReveal` + `headerReveal` keyframes
  // (used by MidasHistoryRowV2 stagger and MidasHistoryDateGroupV2 eyebrow
  // fade-in). Hook deduplicates internally — safe to call here even though
  // MidasV2Layout / MidasPageV2 may also call it elsewhere in the tree.
  useRedesignSetup();

  // Tiny adapter that matches the (key, fallback) shape children use.
  const t = useCallback(
    (key: string, fallback: string) =>
      translate(key as any, { defaultValue: fallback }) as string,
    [translate]
  );

  // ── Slice state ──
  const threads = useAppSelector((s) => s.midasHistoryPage.threads);
  const total = useAppSelector((s) => s.midasHistoryPage.total);
  const offset = useAppSelector((s) => s.midasHistoryPage.offset);
  const hasMore = useAppSelector((s) => s.midasHistoryPage.hasMore);
  const loading = useAppSelector((s) => s.midasHistoryPage.loading);
  const loadingMore = useAppSelector((s) => s.midasHistoryPage.loadingMore);
  const error = useAppSelector((s) => s.midasHistoryPage.error);
  const machineFilter = useAppSelector(
    (s) => s.midasHistoryPage.machineFilter
  );
  const searchQuery = useAppSelector((s) => s.midasHistoryPage.searchQuery);
  const seenMachineIds = useAppSelector(
    (s) => s.midasHistoryPage.seenMachineIds
  );
  // Phase 4.4a: drives the row active-bar indicator. Re-uses the
  // existing /midas-v2 slice's activeThreadId so a thread opened from
  // the chat surface stays highlighted when the user pops over to
  // /midas-v2/history.
  const activeThreadId = useAppSelector((s) => s.midasPage.activeThreadId);

  const machinesMap = useAppSelector((s) => s.customerMachinery.machines);
  const machineryLoading = useAppSelector(
    (s) => s.customerMachinery.loading
  );

  // SEC-REQ-2: Once we hit the ceiling, hide "Load more" even if hasMore is
  // momentarily true. Belt-and-braces guard alongside the slice's check.
  // If `customer_id`/`user_id` params ARE honored by the bot endpoint, file a
  //   P0 bot-side bug — see SEC-REQ-1 verification log in PROGRESS.md.
  const reachedOffsetCeiling =
    offset + HISTORY_PAGE_SIZE > HISTORY_OFFSET_CEILING;
  const showLoadMore = hasMore && !reachedOffsetCeiling;

  // ── Initial load + filter-change refetch ──
  // CRITICAL: This effect must NOT register a cleanup. React fires cleanup on
  // every dependency change, not just unmount — so registering
  // `dispatch(resetHistoryPage())` here turned every chip click into a
  // state-thrash that reset `machineFilter` back to "all" right after the
  // user changed it. That broke the chip filter row entirely (Apr 30 bug:
  // "click Platform → nothing sorts; click All → list empties; click SBL500
  // again → nothing happens"). Unmount-only cleanup lives in the second
  // effect below.
  useEffect(() => {
    dispatch(fetchHistoryThreads({ machineFilter }));
    // Lazy fetch fleet for the chip row
    if (
      flattenMachines(machinesMap).length === 0 &&
      !machineryLoading
    ) {
      dispatch(getMachines({}));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [machineFilter]);

  // ── Unmount-only cleanup (separate effect with [] deps so cleanup fires
  //    ONLY on page unmount, NOT on every filter change). ──
  useEffect(() => {
    return () => {
      dispatch(resetHistoryPage());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Machine name lookup for rows ──
  const machineNameById = useMemo(() => {
    const map: Record<string, string> = {};
    const all = flattenMachines(machinesMap);
    all.forEach((m) => {
      const brand = m.customBrandName || m.manufacturer?.name || "";
      const name = brand
        ? `${brand} ${m.model || ""}`.trim()
        : m.model || "";
      if (m.id && name) map[m.id] = name;
    });
    return map;
  }, [machinesMap]);

  // ── Chips: surface every machine the user has chats for. We use the
  //    sticky `seenMachineIds` set (accumulated by the slice across every
  //    successful fetch on this page) instead of recomputing from the
  //    currently-loaded thread set. Without this, switching to "Platform"
  //    refetches threads filtered by `machine_id=null` and the chip row
  //    collapses to nothing — the SBL500 / Mazak / etc. chips disappear.
  //    Sticky set + always re-add the active filter (defensive) means the
  //    chip the user just clicked never vanishes.
  //
  //    NOTE: D5 was briefly flipped to "full fleet" in 4.3 (commit
  //    46530806) but reverted 2026-05-04 after user testing — full fleet
  //    showed too many machines the user has never chatted about (noisy).
  //    Seen-only matches the mental model that chips are "machines I've
  //    talked about". See PHASE4_HISTORY_FIX_SPEC §6 D5 (REVISED).
  const machineChips: MachineChip[] = useMemo(() => {
    const ids = new Set<string>(seenMachineIds);
    if (
      machineFilter !== "all" &&
      machineFilter !== "platform" &&
      machineFilter
    ) {
      ids.add(machineFilter);
    }
    return Array.from(ids)
      .map((id) => ({
        id,
        label: machineNameById[id] || id.slice(0, 8),
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [seenMachineIds, machineNameById, machineFilter]);

  // ── Client-side search filter over loaded threads ──
  const filteredThreads = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return threads;
    return threads.filter((t) => {
      if (t.title.toLowerCase().includes(q)) return true;
      const mLabel = t.machine_id ? machineNameById[t.machine_id] : null;
      if (mLabel && mLabel.toLowerCase().includes(q)) return true;
      return false;
    });
  }, [threads, searchQuery, machineNameById]);

  // ── Date buckets ──
  const grouped = useMemo(() => {
    const sorted = [...filteredThreads].sort(
      (a, b) =>
        new Date(b.last_message_at || b.created_at).getTime() -
        new Date(a.last_message_at || a.created_at).getTime()
    );
    const buckets: Record<DateBucket, Thread[]> = {
      today: [],
      yesterday: [],
      thisWeek: [],
      lastWeek: [],
      earlier: [],
    };
    sorted.forEach((t) => {
      // Sub-phase 4.5-fix (D7 RE-REVISED): every thread lands in exactly
      // one of the 5 buckets. Empty buckets are filtered out of the
      // render below, so an empty bucket simply doesn't appear.
      const b = bucketOf(t.last_message_at || t.created_at);
      buckets[b].push(t);
    });
    return buckets;
  }, [filteredThreads]);

  // ── Handlers ──
  const handleNewChat = useCallback(() => {
    dispatch(startNewChat());
    dispatch(setActiveThreadId(null));
    dispatch(setMachineContext(null));
    navigate("/midas-v2");
  }, [dispatch, navigate]);

  const handleOpenThread = useCallback(
    (threadId: string) => {
      // Same dispatch pattern the sidebar History tab uses; navigating with the
      // thread already loaded means /midas-v2 renders straight into the right
      // conversation without flashing the welcome state.
      dispatch(loadThread(threadId));
      navigate("/midas-v2");
    },
    [dispatch, navigate]
  );

  const handleMachineFilterChange = useCallback(
    (next: HistoryMachineFilter) => {
      dispatch(setMachineFilter(next));
    },
    [dispatch]
  );

  const handleSearchChange = useCallback(
    (next: string) => {
      dispatch(setSearchQuery(next));
    },
    [dispatch]
  );

  const handleLoadMore = useCallback(() => {
    dispatch(loadMoreHistoryThreads());
  }, [dispatch]);

  const handleRetry = useCallback(() => {
    dispatch(fetchHistoryThreads({ machineFilter }));
  }, [dispatch, machineFilter]);

  // ── Body content ──
  let body: React.ReactNode;
  if (loading && threads.length === 0) {
    body = <MidasHistorySkeletonV2 />;
  } else if (error && threads.length === 0) {
    body = (
      <MidasHistoryEmptyV2
        variant="error"
        title={t("midas.historyPage.errorTitle", "Couldn't load your history")}
        body={t(
          "midas.historyPage.errorBody",
          "Something went wrong. Try again in a moment."
        )}
        ctaLabel={t("midas.historyPage.errorRetry", "Retry")}
        onCtaClick={handleRetry}
      />
    );
  } else if (filteredThreads.length === 0 && threads.length === 0) {
    body = (
      <MidasHistoryEmptyV2
        variant="no-threads"
        title={t("midas.historyPage.emptyTitle", "No conversations yet")}
        body={t(
          "midas.historyPage.emptyBody",
          "Start chatting with MIDAS to build up your history."
        )}
        ctaLabel={t("midas.historyPage.emptyCta", "Start a new chat")}
        onCtaClick={handleNewChat}
      />
    );
  } else if (filteredThreads.length === 0) {
    body = (
      <MidasHistoryEmptyV2
        variant="no-results"
        title={t("midas.historyPage.noResultsTitle", "No matches")}
        body={t(
          "midas.historyPage.noResultsBody",
          "Try a different search term or change the machine filter."
        )}
        ctaLabel={
          searchQuery
            ? t("midas.historyPage.noResultsClear", "Clear search")
            : undefined
        }
        onCtaClick={searchQuery ? () => handleSearchChange("") : undefined}
      />
    );
  } else {
    body = (
      // §5.5 audit: gap between date groups bumped 24px → 32px (gap-6 → gap-8)
      // to match the spec's roomy section break.
      <div className="flex flex-col gap-8">
        {(
          [
            "today",
            "yesterday",
            "thisWeek",
            "lastWeek",
            "earlier",
          ] as DateBucket[]
        ).map(
          // Sub-phase 4.5-fix (D7 RE-REVISED): 5 buckets render in this
          // fixed order. `MidasHistoryDateGroupV2` early-returns null for
          // empty buckets, so we don't need to filter here — and rendering
          // an empty bucket has no DOM cost. `MidasHistoryDateGroupV2`
          // derives its `count` from `threads.length` and renders the
          // right-aligned date label internally.
          (b) => (
            <MidasHistoryDateGroupV2
              key={b}
              bucket={b}
              threads={grouped[b]}
              machineNameById={machineNameById}
              activeThreadId={activeThreadId}
              onOpenThread={handleOpenThread}
              t={t}
            />
          )
        )}
        {showLoadMore && (
          <div className="flex justify-center pt-2">
            {/* Sub-phase 4.6 §3G.2: translucent ink border
                (rgba(44,38,32,0.12)) replaced with opaque warm-stone
                #E2DDD3. Inline fontFamily dropped — font-sora binds Sora
                via Tailwind config. */}
            <button
              type="button"
              onClick={handleLoadMore}
              disabled={loadingMore}
              className="flex h-11 items-center gap-1.5 rounded-md border border-solid border-[#E2DDD3] bg-white px-4 font-sora text-[13px] font-medium text-midas-graphite outline-none transition-colors duration-150 hover:border-midas-terracotta hover:bg-[#FCFAF4] focus-visible:ring-2 focus-visible:ring-midas-terracotta disabled:cursor-not-allowed disabled:opacity-60 motion-reduce:transition-none"
            >
              {loadingMore
                ? t("midas.historyPage.loadingMore", "Loading...")
                : t("midas.historyPage.loadMore", "Load more")}
            </button>
          </div>
        )}
        {error && threads.length > 0 && (
          // Non-fatal "load more" failure — surface inline without losing
          // already-loaded rows.
          <p
            role="alert"
            className="text-center font-sora text-[12px] text-midas-terracotta"
          >
            {t(
              "midas.historyPage.loadMoreError",
              "Couldn't load more. Try again."
            )}
          </p>
        )}
      </div>
    );
  }

  return (
    // Phase 4.1 lifted the V2 sidebar shell into MidasV2Layout.tsx, which also
    // owns the `midas-v2-root` class + `bg-midas-bone` outer surface. This
    // history page is now a child route — it just renders the scrollable body
    // column inside the layout's content slot.
    //
    // Phase 4.2 (3A.3): the bespoke 44px <header> "HISTORY" eyebrow strip that
    // used to sit at the top of this body has been removed. The inline
    // `MIDAS / HISTORY` breadcrumb in the title row replaces it semantically
    // (and is the ONLY breadcrumb on the page — the global app top bar above
    // the V2 layout is unchanged platform chrome, no duplication).
    <div className="flex h-full min-h-0 w-full flex-col overflow-hidden">
      {/* ── Scroll body ── */}
      <main className="flex-1 overflow-y-auto">
        {/* Sub-phase 4.4a: max-width bumped 920 → 1068 so the wider
            hairline row layout (time + title + snippet + machine pill +
            arrow) has room to breathe per designer artboard `R5-0`. */}
        <div className="mx-auto flex w-full max-w-[1068px] flex-col gap-6 px-4 py-8 sm:px-6 md:px-8">
          {/* Title block — Phase 4.2 (§3B):
                row 1: inline `MIDAS / HISTORY` eyebrow above H1
                row 2: H1 + inline `· N` counter (left) + `+ New chat` pill (right)
              Title row uses `flex items-end justify-between` per spec §4.2. */}
          <div className="flex items-end justify-between gap-4 sm:gap-6">
            <div className="flex min-w-0 flex-col gap-1.5">
              {/* Inline eyebrow — only breadcrumb on the page (D1).
                  Sub-phase 4.6 §3G.4: inline fontFamily dropped — font-mono
                  binds JetBrains Mono via Tailwind config. */}
              <span className="font-mono text-[10px] font-medium uppercase leading-3 tracking-[0.18em] text-[#7A7165]">
                {t(
                  "midas.historyPage.eyebrowBreadcrumb",
                  "MIDAS / HISTORY"
                )}
              </span>
              {/* H1 + counter — baseline-aligned inline */}
              <div className="flex items-baseline gap-2">
                <h1
                  // Spec §3B.2 / TR-0:
                  //   desktop = Sora 32/38 weight 600 ls -0.015em
                  //   mobile may keep current 28/34 (per §4.2 acceptance)
                  className="font-sora text-[28px] font-semibold leading-[34px] tracking-[-0.015em] text-midas-graphite md:text-[32px] md:leading-[38px]"
                >
                  {t("midas.historyPage.title", "All your chat history")}
                </h1>
                {/* Inline counter — `· N`, mono 14/18 muted, no "conversations" word (§3B.3) */}
                <span
                  className="font-mono text-[14px] leading-[18px] text-midas-muted-stone"
                  aria-label={`${total} ${
                    total === 1
                      ? t(
                          "midas.historyPage.conversationOne",
                          "conversation"
                        )
                      : t(
                          "midas.historyPage.conversationMany",
                          "conversations"
                        )
                  }`}
                >
                  {`· ${total}`}
                </span>
              </div>
            </div>
            {/* `+ New chat` pill — wires to existing handleNewChat (§3B.4 / TT-0).
                Sub-phase 4.6 §3G.4: inline fontFamily dropped — font-sora
                binds Sora via Tailwind config. */}
            <button
              type="button"
              onClick={handleNewChat}
              className="flex flex-shrink-0 items-center gap-2 rounded-[10px] bg-[#1A1614] px-3.5 py-2.5 font-sora text-[13px] font-medium leading-4 tracking-[-0.005em] text-[#FAF7F0] outline-none transition-all duration-150 hover:-translate-y-0.5 hover:shadow-md focus-visible:ring-2 focus-visible:ring-midas-terracotta focus-visible:ring-offset-2 focus-visible:ring-offset-midas-bone motion-reduce:transition-none motion-reduce:hover:translate-y-0"
              aria-label={t("midas.historyPage.newChat", "+ New chat")}
            >
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
                style={{ flexShrink: 0 }}
              >
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
              {t("midas.historyPage.newChatShort", "New chat")}
            </button>
          </div>

          {/* Search */}
          <MidasHistorySearchV2
            value={searchQuery}
            onChange={handleSearchChange}
            showKeyboardHint={!isPhone}
            placeholder={t(
              "midas.historyPage.searchPlaceholder",
              "Search by title or machine"
            )}
            ariaLabel={t(
              "midas.historyPage.searchAriaLabel",
              "Search conversations"
            )}
            clearLabel={t("midas.historyPage.searchClear", "Clear search")}
          />

          {/* Machine filter chips + bottom divider (§3B.5 / TM-0) — separates
              hero block from list. Chip row internals get rebuilt in 4.3; the
              divider lives at the parent level so it stays put across that
              rebuild. */}
          <div className="border-b border-[#E2DDD3] pb-6">
            <MidasHistoryMachineFiltersV2
              active={machineFilter}
              machines={machineChips}
              onChange={handleMachineFilterChange}
              t={t}
            />
          </div>

          {/* Body
              When a filter re-fetch is in flight AND we still have stale
              rows (because the slice no longer wipes threads on filter
              change — see midasHistoryPageSlice.ts setMachineFilter
              comment), dim the list to signal "refreshing". This replaces
              the old empty→skeleton→rows triple-flash with a single
              opacity transition. `aria-busy` keeps screen readers in sync.
              motion-reduce keeps the dim instant rather than fading. */}
          <div
            aria-busy={loading && threads.length > 0}
            className="transition-opacity duration-150 motion-reduce:transition-none"
            style={{ opacity: loading && threads.length > 0 ? 0.55 : 1 }}
          >
            {body}
          </div>
        </div>
      </main>
    </div>
  );
};

export default MidasHistoryPageV2;
